// @react-compiler enable
import SessionElapsedTime from '@github-ui/agent-sessions/components/SessionElapsedTime'
import {getSessionStatusIcon} from '@github-ui/agent-sessions/components/StatusIcon'
import {useAgentInfo} from '@github-ui/agent-sessions/hooks/use-agent-info'
import {generateTaskPathForRepo} from '@github-ui/agent-sessions/utils/generate-task-path'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {testIdProps} from '@github-ui/test-id-props'
import {CopilotIcon} from '@primer/octicons-react'
import {Link} from '@primer/react'
import {memo, useEffect, useMemo, useRef} from 'react'

import type {AgentTask} from '../../api/memex-items/contracts'
import {useTableCopilotTasks} from '../../features/copilot-tasks/use-table-copilot-tasks'
import type {MemexItemModel} from '../../models/memex-item-model'
import {shouldShowAgentTaskPill} from '../fields/agent-task-pill'
import {AgentTaskPopover} from '../fields/agent-task-popover'
import styles from './agent-task-sub-row.module.css'
import {focusAgentTaskRow, TableFocusType, useStableTableNavigation, useTableNavigation} from './navigation'

interface AgentTaskSubRowProps {
  model: MemexItemModel
  /** Individual task data to render (used when multipleSubrows flag is on). */
  task?: AgentTask
  /** TanStack row ID of the parent row, used for table navigation focus tracking. */
  rowId?: string
  /** Hierarchy depth of the parent row (0 for top-level items). */
  depth?: number
  /** Whether the table hierarchy (Show hierarchy toggle) is enabled. */
  hierarchyEnabled?: boolean
  /** Total width (px) of visible columns before the Title column, excluding the drag handle. */
  titleColumnOffset?: number
}

function AgentTaskAgentIcon({agentId}: {agentId?: number}) {
  const {data: agentInfo} = useAgentInfo(agentId ?? null)

  const avatarUrl = agentInfo?.avatar_url
  const displayName = agentInfo?.display_name || 'Copilot'
  const isCopilot = !agentId || displayName.toLowerCase() === 'copilot'

  if (isCopilot || !avatarUrl) {
    return <CopilotIcon size={16} />
  }

  return <GitHubAvatar src={avatarUrl} alt={displayName} size={16} />
}

export const AgentTaskSubRow = memo(function AgentTaskSubRow({
  model,
  task,
  rowId,
  depth = 0,
  hierarchyEnabled = false,
  titleColumnOffset = 0,
}: AgentTaskSubRowProps) {
  const {copilotTasksEnabled, multipleSubrowsEnabled} = useTableCopilotTasks()
  const linkRef = useRef<HTMLAnchorElement>(null)
  const {state} = useTableNavigation()
  const {navigationDispatch} = useStableTableNavigation()

  const taskId = task?.id ?? model.agentTaskId
  const taskUrl = useMemo(() => {
    if (!taskId) return undefined
    // Prefer the individual task's repo NWO (for multi-subrow), then the model-level
    // flat field, then fall back to the item's (issue's) repo NWO.
    const repositoryNwo = task?.repoNwo ?? model.agentTaskRepoNwo ?? (model.getNameWithOwner()?.join('/') || undefined)
    return generateTaskPathForRepo({taskId, repositoryNwo, linkToSessionsInRepo: true}) || undefined
  }, [taskId, task?.repoNwo, model])

  // Focus the link when table navigation state targets this agent task row
  useEffect(() => {
    if (
      state.focus?.type === TableFocusType.AGENT_TASK_ROW &&
      state.focus.details?.parentRowId === rowId &&
      state.focus.details?.taskId === taskId
    ) {
      linkRef.current?.focus()
    }
  }, [state.focus, rowId, taskId])

  if (!copilotTasksEnabled) return null

  if (multipleSubrowsEnabled && task) {
    if (!shouldShowAgentTaskPill(task.state, task.completedAt)) return null

    const statusIcon = getSessionStatusIcon(task.state)

    return (
      <div
        className={styles.AgentTaskSubRow}
        style={
          {
            '--agent-task-depth': depth,
            '--hierarchy-enabled': hierarchyEnabled ? 1 : 0,
            '--title-column-offset': titleColumnOffset,
          } as React.CSSProperties
        }
        {...testIdProps('agent-task-sub-row')}
      >
        <AgentTaskPopover
          title={task.title}
          nwo={model.getNameWithOwnerReference()}
          state={task.state}
          createdAt={task.createdAt}
          completedAt={task.completedAt}
          className={styles.popoverContentWrapper}
        >
          <span className={styles.statusIcon}>{statusIcon}</span>
          <span className={styles.agentIcon}>
            <AgentTaskAgentIcon agentId={task.agentId} />
          </span>
          {task.title &&
            (taskUrl ? (
              <Link
                ref={linkRef}
                href={taskUrl}
                className={styles.title}
                onFocus={() => rowId && navigationDispatch(focusAgentTaskRow(rowId, taskId))}
              >
                {task.title}
              </Link>
            ) : (
              <span className={styles.title}>{task.title}</span>
            ))}
          {task.createdAt && (
            <span className={styles.elapsedTime}>
              <SessionElapsedTime createdAt={task.createdAt} completedAt={task.completedAt} state={task.state} />
            </span>
          )}
        </AgentTaskPopover>
      </div>
    )
  }

  // Legacy single-task path (memex_multiple_subrows off)
  if (!shouldShowAgentTaskPill(model.agentTaskState, model.agentTaskCompletedAt)) return null
  const agentTaskState = model.agentTaskState
  if (!agentTaskState) return null

  const statusIcon = getSessionStatusIcon(agentTaskState)
  const title = model.agentTaskTitle

  return (
    <div
      className={styles.AgentTaskSubRow}
      style={
        {
          '--agent-task-depth': depth,
          '--hierarchy-enabled': hierarchyEnabled ? 1 : 0,
          '--title-column-offset': titleColumnOffset,
        } as React.CSSProperties
      }
      {...testIdProps('agent-task-sub-row')}
    >
      <AgentTaskPopover
        title={title}
        nwo={model.getNameWithOwnerReference()}
        state={agentTaskState}
        createdAt={model.agentTaskCreatedAt}
        completedAt={model.agentTaskCompletedAt}
        className={styles.popoverContentWrapper}
        href={taskUrl}
      >
        <span className={styles.statusIcon}>{statusIcon}</span>
        <span className={styles.agentIcon}>
          <AgentTaskAgentIcon agentId={model.agentTaskAgentId} />
        </span>
        {title &&
          (taskUrl ? (
            <Link
              ref={linkRef}
              href={taskUrl}
              className={styles.title}
              onFocus={() => rowId && navigationDispatch(focusAgentTaskRow(rowId, taskId))}
            >
              {title}
            </Link>
          ) : (
            <span className={styles.title}>{title}</span>
          ))}
        {model.agentTaskCreatedAt && (
          <span className={styles.elapsedTime}>
            <SessionElapsedTime
              createdAt={model.agentTaskCreatedAt}
              completedAt={model.agentTaskCompletedAt}
              state={agentTaskState}
            />
          </span>
        )}
      </AgentTaskPopover>
    </div>
  )
})
